import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tag } from '@/components/Ui/Tag';
import type { FormField, FormSpec } from '@/lib/form-specs';
import classes from './FieldEditor.module.css';
import { isChoice, kindOf, PALETTE } from './model';

const KNOWN_KINDS = new Set<string>([...PALETTE.flatMap((g) => g.kinds), 'submit']);

export function kindLabel(t: TFunction, field: FormField): string {
  const kind = kindOf(field);
  if (kind === 'trap') return t('fields.typeTag.trap');
  return KNOWN_KINDS.has(kind) ? t(`fields.kind.${kind}`) : field.field;
}

function ruleTag(t: TFunction, field: FormField, trap: boolean) {
  const rule = field.on_fail ?? 'pass';
  const variant = rule === 'reject' || rule === 'trash' ? 'risk' : 'neutral';
  if (trap && rule !== 'pass') {
    const key = { reject: 'trapReject', spam: 'trapSpam', trash: 'trapTrash' }[rule];
    return <Tag variant={variant}>{t(`fields.rule.${key}`)}</Tag>;
  }
  return <Tag variant={variant}>{t(`fields.rule.${rule}`)}</Tag>;
}

function preview(t: TFunction, field: FormField, trap: boolean): string {
  if (trap) return t('fields.trapPreview');
  if (field.field === 'hidden') {
    return field.value !== undefined && field.value !== ''
      ? `${t('fields.hiddenPreview')}: ${field.value}`
      : t('fields.hiddenPreview');
  }
  if (isChoice(field)) return (field.options ?? '').split(',').filter(Boolean).join(', ');
  return field.placeholder ?? '';
}

interface CardProps {
  fieldKey: string;
  field: FormField;
  selected: boolean;
  onSelect: () => void;
}

function FieldCard({ fieldKey, field, selected, onSelect }: CardProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: fieldKey });
  const trap = kindOf(field) === 'trap';
  const title = trap ? t('fields.trapCardLabel') : field.label || field.name;

  const fakeClass = [
    classes.fake,
    trap ? classes.fakeTrap : '',
    field.field === 'textarea' ? classes.fakeTall : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={setNodeRef}
      className={`${classes.card} ${selected ? classes.cardOn : ''}`}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        position: 'relative',
        zIndex: isDragging ? 2 : undefined,
      }}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className={classes.handle}
        aria-label={t('fields.move', { name: field.name })}
        {...attributes}
        {...listeners}
      >
        <svg width="14" height="18" viewBox="0 0 14 18" aria-hidden="true">
          <g fill="currentColor">
            <circle cx="4" cy="3" r="1.6" />
            <circle cx="10" cy="3" r="1.6" />
            <circle cx="4" cy="9" r="1.6" />
            <circle cx="10" cy="9" r="1.6" />
            <circle cx="4" cy="15" r="1.6" />
            <circle cx="10" cy="15" r="1.6" />
          </g>
        </svg>
      </button>
      <button type="button" className={classes.select} aria-pressed={selected} onClick={onSelect}>
        <span className={classes.cardMain}>
          <span className={classes.cardTitle}>
            <strong>{title}</strong>
            <span className={`${classes.mono} ${classes.sub}`}>{field.name}</span>
          </span>
          <span className={fakeClass}>{preview(t, field, trap) || ' '}</span>
        </span>
        <span className={classes.cardTags}>
          <Tag>{kindLabel(t, field)}</Tag>
          {field.required && !trap && <Tag variant="blue">{t('fields.requiredTag')}</Tag>}
          {ruleTag(t, field, trap)}
        </span>
      </button>
    </div>
  );
}

export interface FieldCanvasProps {
  spec: FormSpec;
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onMove: (from: number, to: number) => void;
  /** The form-level panel under the cards. */
  footer: ReactNode;
}

export function FieldCanvas({ spec, selectedKey, onSelect, onMove, footer }: FieldCanvasProps) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const keys = Object.keys(spec.fields);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = keys.indexOf(String(active.id));
    const to = keys.indexOf(String(over.id));
    if (from >= 0 && to >= 0) onMove(from, to);
  };

  return (
    <section className={classes.canvas} aria-label={t('fields.canvasLabel')}>
      {keys.length === 0 ? (
        <div className={`${classes.panel} ${classes.empty}`}>{t('fields.canvasEmpty')}</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={keys} strategy={verticalListSortingStrategy}>
            {keys.map((key) => (
              <FieldCard
                key={key}
                fieldKey={key}
                field={spec.fields[key]}
                selected={key === selectedKey}
                onSelect={() => onSelect(key)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
      {footer}
    </section>
  );
}
